import getArticles from "@/api/requests/wordpress/getArticles";
import getMenuItems from "@/api/requests/wordpress/getMenuItems";
import {
  Article,
  ArticleExcerpt,
  MenuItems,
  RegionRestriction,
} from "@/custom-types/Wordpress";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import {
  redirectToPreviewPage,
  regionRestrictionRedirect,
} from "@/utils/news/redirect";
import { getCountryCodeHeader } from "@/utils/requestContext";

import ArticleLayout from "@/components/news/Layouts/ArticleLayout";

type ArticlePageProps = {
  article: Article;
  relatedArticles: ArticleExcerpt[];
  menuItems: MenuItems;
};

const ArticlePage: React.FC<ArticlePageProps> = ({
  article,
  menuItems,
  relatedArticles,
}) => {
  return (
    <ArticleLayout
      article={article}
      menuItems={menuItems}
      relatedArticles={relatedArticles}
    />
  );
};

export const getServerSideProps = createGetServerSideProps<
  ArticlePageProps,
  {
    articleSlug: string;
  }
>(async (context) => {
  const {
    params: { articleSlug } = {
      articleSlug: "",
    },
    query: { preview_id, preview, page_id },
    resolvedUrl,
  } = context;

  const isPreview =
    (preview_id || page_id) && (preview === "true" || preview === "1");
  if (isPreview) {
    return redirectToPreviewPage((preview_id || page_id) as string, "article");
  }

  const countryCode = getCountryCodeHeader(context.req.headers);

  const articlesResponse = getArticles(
    countryCode,
    { slug: articleSlug },
    "1",
    "1",
    "full",
  );
  const menuItemsResponse = getMenuItems(
    countryCode === "CA" ? "canada" : "global",
  );

  const { articles } = await articlesResponse;
  const article = articles?.[0] as Article;

  if (!article) {
    return {
      notFound: true,
    };
  }

  const { regionRestriction, regionRestrictionRedirectUrl } = article;
  if (
    regionRestrictionRedirectUrl ||
    (regionRestriction !== RegionRestriction.WW &&
      regionRestriction !== countryCode)
  ) {
    return regionRestrictionRedirect(
      resolvedUrl,
      regionRestriction,
      regionRestrictionRedirectUrl,
    );
  }

  const menuItems = await menuItemsResponse;

  let relatedArticles: ArticleExcerpt[] = [];
  if (article.categoryIds?.[0]) {
    const { articles } = await getArticles(
      countryCode,
      { categories: `${article.categoryIds?.[0]}` },
      "1",
      "4",
    );
    relatedArticles = articles as ArticleExcerpt[];
  }

  return {
    props: {
      article,
      menuItems,
      relatedArticles,
    },
  };
});

export default ArticlePage;
